/* @jsx h */
import { defineComponent, h } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useTrpc } from "@app/vue/providers/index";
import { CoinRequirement } from "@lemmonhq/types";

export type CoinRequirementEditorProps = {
  value: CoinRequirement;
  onChange: (requirement: CoinRequirement) => void;
};

// Picks over real coin programs rather than the old free-text `coin_type`.
// Eligibility and the FIFO debit key off `coin_program_id`, so a free-text slug
// that matched no program made the whole tier silently inert. See
// docs/plans/loyalty/redemption_wiring.md and manager_ui.md §6.
export const CoinRequirementEditor =
  defineComponent<CoinRequirementEditorProps>({
    name: "CoinRequirementEditor",
    props: ["value", "onChange"],
    setup(props) {
      const trpc = useTrpc();
      const programsQuery = useQuery({
        queryKey: ["coinPrograms", "requirement-picker"],
        queryFn: () => trpc.coinPrograms.list.query({ enabled_only: true }),
      });

      const selectProgram = (programId: string) => {
        const program = (programsQuery.data.value ?? []).find(
          (p) => p.id === programId,
        );
        if (!program) return;
        // Carry coin_type alongside the id: the wallet pass renderer reads it
        // without a join, and it is the display label.
        props.onChange({
          ...props.value,
          coin_program_id: program.id,
          coin_type: program.coin_type,
        });
      };

      const updateAmount = (amount: number) => {
        props.onChange({ ...props.value, amount });
      };

      return () => {
        const programs = programsQuery.data.value ?? [];
        const hasPrograms = programs.length > 0;

        return (
          <div class="card border">
            <div class="card-body p-4">
              <h4 class="card-title text-accent text-lg flex items-center gap-2">
                <div class="w-2 h-2 bg-warning rounded-full"></div>
                Coin Requirement
              </h4>

              {programsQuery.isLoading.value ? (
                <div class="mt-4">
                  <span class="loading loading-spinner loading-sm"></span>
                </div>
              ) : !hasPrograms ? (
                <div class="alert alert-warning mt-4 text-sm">
                  No coin programs yet. Create one under Coin Programs before
                  adding a coin requirement.
                </div>
              ) : (
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">Coin Program</span>
                    </label>
                    <select
                      value={props.value.coin_program_id ?? ""}
                      onChange={(e) =>
                        selectProgram((e.target as HTMLSelectElement).value)
                      }
                      class="select select-bordered focus:select-primary"
                    >
                      <option value="" disabled>
                        Select a program…
                      </option>
                      {programs.map((program) => (
                        <option key={program.id} value={program.id}>
                          {program.name} ({program.coin_label})
                        </option>
                      ))}
                    </select>
                    <label class="label">
                      <span class="label-text-alt">
                        Which program's coins this tier spends
                      </span>
                    </label>
                  </div>

                  <div class="form-control">
                    <label class="label">
                      <span class="label-text font-medium">Required Amount</span>
                    </label>
                    <input
                      type="number"
                      value={props.value.amount}
                      onInput={(e) =>
                        updateAmount(
                          parseInt((e.target as HTMLInputElement).value) || 0,
                        )
                      }
                      class="input input-bordered focus:input-primary"
                      min="1"
                      placeholder="10"
                    />
                    <label class="label">
                      <span class="label-text-alt">
                        Coins needed to redeem this tier
                      </span>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      };
    },
  });
